import { useState, useEffect, useCallback } from "react";
import { Users, Plus, Pencil, Trash2, Shield, Eye, UserCog, Loader2, X, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuth() {
  const token = localStorage.getItem("admin_token") || localStorage.getItem("app_token");
  return token ? { authorization: `Bearer ${token}` } : {};
}

const ROLE_LABELS = {
  super_admin: { label: "Super Admin", color: "#DC2626", icon: Shield },
  admin: { label: "MyGoals Admin", color: "#194449", icon: UserCog },
  microsoft_manager: { label: "Microsoft Manager", color: "#2563EB", icon: Building2 },
  user: { label: "MyGoals User", color: "#64748B", icon: Eye },
  report_viewer: { label: "Report Viewer", color: "#94A3B8", icon: Eye },
};

const ALLOWED_DOMAINS = ["technofocus.in", "technofocus.co", "microsoft.com"];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "user",
  });

  const [editUser, setEditUser] = useState({
    name: "",
    role: "",
    status: "",
    password: "",
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, meRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: getAuth() }),
        axios.get(`${API}/admin/users/me`, { headers: getAuth() }),
      ]);
      setUsers(usersRes.data);
      setCurrentUser(meRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("You don't have permission to manage users");
      } else {
        toast.error("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Please fill all required fields");
      return;
    }
    const domain = newUser.email.split("@")[1]?.toLowerCase();
    if (!ALLOWED_DOMAINS.includes(domain)) {
      toast.error(`Email must be from: ${ALLOWED_DOMAINS.join(", ")}`);
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users`, newUser, { headers: getAuth() });
      toast.success("User created successfully");
      setShowAddDialog(false);
      setNewUser({ email: "", password: "", name: "", role: "user" });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    const updates = {};
    if (editUser.name && editUser.name !== selectedUser.name) updates.name = editUser.name;
    if (editUser.role && editUser.role !== selectedUser.role) updates.role = editUser.role;
    if (editUser.status && editUser.status !== selectedUser.status) updates.status = editUser.status;
    if (editUser.password) updates.password = editUser.password;

    if (Object.keys(updates).length === 0) {
      toast.info("No changes to save");
      return;
    }

    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}`, updates, { headers: getAuth() });
      toast.success("User updated successfully");
      setShowEditDialog(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${user.user_id}`, { headers: getAuth() });
      toast.success("User deleted");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditUser({
      name: user.name,
      role: user.role,
      status: user.status,
      password: "",
    });
    setShowEditDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#194449]" />
      </div>
    );
  }

  if (currentUser?.role !== "super_admin") {
    return (
      <div className="bg-white rounded-xl border border-black/5 p-8 text-center">
        <Shield className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[#091F2C] mb-2">Access Restricted</h3>
        <p className="text-sm text-[#64748B]">Only Super Admins can manage users.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#091F2C]">User Management</h2>
          <p className="text-sm text-[#64748B]">Manage admin users and their permissions</p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-[#194449] hover:bg-[#07641D] text-white rounded-full"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      {/* Domain Info */}
      <div className="bg-[#F4F3F5] rounded-lg p-4 text-sm">
        <span className="font-medium text-[#091F2C]">Allowed email domains: </span>
        <span className="text-[#64748B]">{ALLOWED_DOMAINS.join(", ")}</span>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F3F5]">
              <TableHead className="text-xs font-medium text-[#64748B]">User</TableHead>
              <TableHead className="text-xs font-medium text-[#64748B]">Role</TableHead>
              <TableHead className="text-xs font-medium text-[#64748B]">Status</TableHead>
              <TableHead className="text-xs font-medium text-[#64748B]">Created By</TableHead>
              <TableHead className="text-xs font-medium text-[#64748B]">Created At</TableHead>
              <TableHead className="text-xs font-medium text-[#64748B] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => {
              const roleInfo = ROLE_LABELS[user.role] || ROLE_LABELS.report_viewer;
              const RoleIcon = roleInfo.icon;
              const isCurrentUser = user.email === currentUser?.email;
              return (
                <TableRow key={user.user_id} className="hover:bg-[#F4F3F5]/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#194449]/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-[#194449]" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-[#091F2C]">
                          {user.name} {isCurrentUser && <span className="text-xs text-[#64748B]">(You)</span>}
                        </div>
                        <div className="text-xs text-[#64748B]">{user.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className="text-xs font-medium flex items-center gap-1 w-fit"
                      style={{ backgroundColor: `${roleInfo.color}15`, color: roleInfo.color, border: "none" }}
                    >
                      <RoleIcon className="w-3 h-3" />
                      {roleInfo.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`text-xs ${user.status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                    >
                      {user.status || "active"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-[#64748B]">{user.created_by || "System"}</TableCell>
                  <TableCell className="text-xs text-[#64748B]">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => openEditDialog(user)}
                        data-testid={`edit-user-${user.user_id}`}
                      >
                        <Pencil className="w-4 h-4 text-[#64748B]" />
                      </Button>
                      {!isCurrentUser && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-red-50"
                          onClick={() => handleDeleteUser(user)}
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs">Full Name *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="John Doe"
                className="mt-1"
                data-testid="new-user-name"
              />
            </div>
            <div>
              <Label className="text-xs">Email Address *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@technofocus.in"
                className="mt-1"
                data-testid="new-user-email"
              />
              <p className="text-[10px] text-[#64748B] mt-1">Must be from: {ALLOWED_DOMAINS.join(", ")}</p>
            </div>
            <div>
              <Label className="text-xs">Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Minimum 8 characters"
                className="mt-1"
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="mt-1" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin (Full Access)</SelectItem>
                  <SelectItem value="admin">MyGoals Admin (Resources & Connectors)</SelectItem>
                  <SelectItem value="microsoft_manager">Microsoft Manager (Resources, Staging & Analytics)</SelectItem>
                  <SelectItem value="user">MyGoals User (Analytics Only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowAddDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#194449] hover:bg-[#07641D] text-white"
                onClick={handleCreateUser}
                disabled={actionLoading}
                data-testid="create-user-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create User"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 pt-2">
              <div className="bg-[#F4F3F5] rounded-lg p-3 text-sm">
                <span className="font-medium">{selectedUser.email}</span>
              </div>
              <div>
                <Label className="text-xs">Full Name</Label>
                <Input
                  value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                  className="mt-1"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">MyGoals Admin</SelectItem>
                    <SelectItem value="microsoft_manager">Microsoft Manager</SelectItem>
                    <SelectItem value="user">MyGoals User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={editUser.status} onValueChange={(v) => setEditUser({ ...editUser, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-user-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">New Password (leave blank to keep current)</Label>
                <Input
                  type="password"
                  value={editUser.password}
                  onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                  placeholder="Enter new password"
                  className="mt-1"
                  data-testid="edit-user-password"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowEditDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#194449] hover:bg-[#07641D] text-white"
                  onClick={handleUpdateUser}
                  disabled={actionLoading}
                  data-testid="save-user-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
